import { useEffect, useState, useCallback } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  RefreshCw,
  Calendar,
  Clock,
  Box,
  Tag,
  Barcode,
  Scale,
  CheckCircle2,
  FileSpreadsheet,
  // Sun,
  // Moon,
} from "lucide-react";
import KPICard from "../../components/charts/KPICard";
import { productAPI, weightAPI } from "../../services/api";
import { exportSpcToExcel } from "../../utils/exportExcel";
import { useTheme } from "../../context/ThemeContext";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
  XAxis,
  YAxis,
  ReferenceLine,
  ComposedChart,
  Bar,
} from "recharts";

const todayStr = () => new Date().toISOString().slice(0, 10);

// 4-state verdict -> color mapping (matches spcCalculator.js's verdict() thresholds exactly)
function verdictColor(v) {
  switch (v) {
    case "POOR / NOT CAPABLE":
      return "var(--danger, #FF4D6A)";
    case "MARGINAL":
      return "#FFB020";
    case "GOOD":
      return "var(--success, #00E096)";
    case "EXCELLENT":
      return "var(--success, #00E096)";
    default:
      return "var(--text3, #5A6178)";
  }
}

export default function CompanyDashboard() {
  const [products, setProducts] = useState([]);
  // const { theme, toggleTheme } = useTheme();
  const { theme } = useTheme();
  const [selectedProductId, setSelectedProductId] = useState("");
  const [date, setDate] = useState(todayStr());
  const [fromTime, setFromTime] = useState("");
  const [toTime, setToTime] = useState("");

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [live, setLive] = useState(true);

  // Single source of truth — same endpoint the Template page uses.
  // No Cp/Cpk/histogram/control-chart math happens in this file anymore.
  const [data, setData] = useState(null);
  const [totalCount, setTotalCount] = useState(0); // ajay:code 03-08-26
  const [totalPassCount, setTotalPassCount] = useState(0); // ajay:code 04-08-26
  const [totalFailCount, setTotalFailCount] = useState(0); // ajay:code 04-08-26

  useEffect(() => {
    if (selectedProductId) {
      localStorage.setItem("weightqa_dashboard_product_id", selectedProductId);
    }
  }, [selectedProductId]);

  // ── Fetch product list once ──

  useEffect(() => {
    productAPI
      .list()
      .then((res) => {
        const activeOnly = (res.data || []).filter(
          (p) => p.status === "Active",
        );
        setProducts(activeOnly);
        const saved = localStorage.getItem("weightqa_dashboard_product_id");
        const savedStillValid =
          saved && activeOnly.some((p) => String(p.id) === saved);
        if (savedStillValid) {
          setSelectedProductId(saved);
        } else if (activeOnly.length > 0) {
          setSelectedProductId(String(activeOnly[0].id));
        } else {
          setLoading(false);
        }
      })
      .catch((e) => {
        setErr("Failed to load part: " + e.message);
        setLoading(false);
      });
  }, []);

  // ── Fetch SPC data (product + date/time filters) ──
  const fetchDashboardData = useCallback(
    async (silent = false) => {
      if (!selectedProductId) return;
      if (!silent) setLoading(true);
      try {
        const params = { date, product_id: selectedProductId };
        if (fromTime) params.from_time = fromTime;
        if (toTime) params.to_time = toTime;

        const res = await weightAPI.spcData(params);
        setData(res.data);
        setLastUpdated(new Date());
        setErr("");
      } catch (e) {
        setErr(e.message || "Error fetching dashboard data");
      } finally {
        setLoading(false);
      }
    },
    [selectedProductId, date, fromTime, toTime],
  );

  // KPI card ke liye total count — bina 125-limit ke, WeightHistory jaisa hi endpoint  ajay:code 03-08-26
  const fetchTotalCount = useCallback(async () => {
    if (!selectedProductId) return;
    try {
      let from = `${date} 00:00:00`;
      let to = `${date} 23:59:59`;
      if (fromTime) from = `${date} ${fromTime}:00`;
      if (toTime) to = `${date} ${toTime}:59`;

      const baseParams = { product_id: selectedProductId, from, to, limit: 1 };

      const [totalRes, passRes, failRes] = await Promise.all([
        weightAPI.list(baseParams),
        weightAPI.list({ ...baseParams, result: "Pass" }),
        weightAPI.list({ ...baseParams, result: "Fail" }),
      ]);

      setTotalCount(totalRes.data.total || 0);
      setTotalPassCount(passRes.data.total || 0);
      setTotalFailCount(failRes.data.total || 0);
    } catch (e) {
      // KPI count fail ho to chup rehne do — baki dashboard kaam karta rahe
    }
  }, [selectedProductId, date, fromTime, toTime]);

  useEffect(() => {
    fetchTotalCount();
  }, [fetchTotalCount]);

  useEffect(() => {
    if (selectedProductId) fetchDashboardData(false);
  }, [selectedProductId, date, fromTime, toTime, fetchDashboardData]);

  // Live mode: only auto-refresh when today's date + no time filter (same rule as Template page)
  // useEffect(() => {
  //   const isLiveMode = live && date === todayStr() && !fromTime && !toTime;
  //   if (!isLiveMode || !selectedProductId) return;
  //   const interval = setInterval(() => {
  //     if (!document.hidden) fetchDashboardData(true);
  //   }, 1000);
  //   return () => clearInterval(interval);
  // }, [live, date, fromTime, toTime, selectedProductId, fetchDashboardData]);

  useEffect(() => {
    const isLiveMode = live && date === todayStr() && !fromTime && !toTime;
    if (!isLiveMode || !selectedProductId) return;

    const interval = setInterval(() => {
      if (!document.hidden) {
        fetchDashboardData(true);
        fetchTotalCount();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [
    live,
    date,
    fromTime,
    toTime,
    selectedProductId,
    fetchDashboardData,
    fetchTotalCount,
  ]);

  // ── Everything below is DERIVED from data.spc / data.product / data.readings.
  // Nothing is calculated locally — it all comes straight from the backend. ──
  const product = data?.product;
  const spc = data?.spc;
  const readings = data?.readings || [];
  const latest = data?.latest;
  const last = latest;

  const selectedProduct =
    products.find((p) => String(p.id) === String(selectedProductId)) || product;

  const LSL = Number(product?.lsl ?? selectedProduct?.min_weight ?? 0);
  const USL = Number(product?.usl ?? selectedProduct?.max_weight ?? 0);
  const target = product?.target ?? ((LSL + USL) / 2).toFixed(3);
  const productImage = product?.image || null; // already a full URL from spc-data

  const checksCount = data?.count || 0;
  const passed = readings.filter((e) => e.result === "Pass").length;
  const failed = readings.filter((e) => e.result === "Fail").length;
  const avgWeight =
    readings.length > 0
      ? (
          readings.reduce((sum, e) => sum + Number(e.weight), 0) /
          readings.length
        ).toFixed(2)
      : "—";
  const passRate =
    checksCount > 0 ? ((passed / checksCount) * 100).toFixed(1) : "0.0";

  // Cp/Cpk, sigma, grand mean — ALL come from spc (backend)
  const insufficientData = !spc || spc.insufficientData;
  const cp = !insufficientData ? spc.cp : null;
  const cpk = !insufficientData ? spc.cpk : null;
  const cpVerdict = !insufficientData ? spc.cpVerdict : null; // POOR / NOT CAPABLE | MARGINAL | GOOD | EXCELLENT
  const cpkVerdict = !insufficientData ? spc.cpkVerdict : null;

  const currentMean = !insufficientData ? spc.xBarBar : (LSL + USL) / 2;
  const sigmaDisplay = !insufficientData ? spc.sigma : "—";
  const grandMeanDisplay = !insufficientData ? spc.xBarBar : "—";

  const pieData = [
    { name: "In Range", value: passed, color: "var(--success)" },
    { name: "Out of Range", value: failed, color: "var(--danger)" },
  ];

  // Control chart — RAW individual readings over time (not subgroup averages)
  // const controlChartData = readings.map((e) => ({
  //   time: new Date(e.recorded_at).toLocaleTimeString([], {
  //     hour: "2-digit",
  //     minute: "2-digit",
  //     second: "2-digit",
  //   }),
  //   weight: Number(e.weight),
  // }));
  // ajay:code 06-08-26  graph show only 25 data X bar controller
  const controlChartData = readings
    .slice(-25) // Show only the latest 25 readings
    .map((e) => ({
      time: new Date(e.recorded_at).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
      weight: Number(e.weight),
    }));

  // const getYDomain = () => {
  //   const vals = readings.map((e) => Number(e.weight));
  //   if (!isNaN(LSL)) vals.push(LSL);
  //   if (!isNaN(USL)) vals.push(USL);
  //   if (!isNaN(currentMean)) vals.push(currentMean);
  //   if (vals.length === 0) return ["auto", "auto"];
  //   const minVal = Math.min(...vals);
  //   const maxVal = Math.max(...vals);
  //   const diff = maxVal - minVal;
  //   const padding = diff === 0 ? 2 : diff * 0.15;
  //   return [+(minVal - padding).toFixed(3), +(maxVal + padding).toFixed(3)];
  // };

  // ajay:code 06-08-26 graph show only 25 data
  const getYDomain = () => {
    const vals = controlChartData.map((e) => e.weight);

    if (!isNaN(LSL)) vals.push(LSL);
    if (!isNaN(USL)) vals.push(USL);
    if (!isNaN(currentMean)) vals.push(currentMean);

    if (vals.length === 0) return ["auto", "auto"];

    const minVal = Math.min(...vals);
    const maxVal = Math.max(...vals);
    const diff = maxVal - minVal;
    const padding = diff === 0 ? 2 : diff * 0.15;

    return [+(minVal - padding).toFixed(3), +(maxVal + padding).toFixed(3)];
  };

  // Histogram — bin counts straight from spc.histogram (backend). "curve" is a
  // cosmetic-only bell overlay, not authoritative — fine to compute client-side for visuals.
  const histogram = (spc?.histogram?.bins || []).map((b) => {
    const mid = (b.first + b.last) / 2;
    const sig = spc?.sigma || (USL - LSL) / 6 || 0.5;
    const z = (mid - currentMean) / sig;
    const maxCount = Math.max(...spc.histogram.bins.map((x) => x.freq), 5);
    return {
      range: mid.toFixed(2),
      count: b.freq,
      curve: !insufficientData
        ? Math.round(maxCount * Math.exp(-0.5 * z * z) * 10) / 10
        : null,
    };
  });

  const [imgError, setImgError] = useState(false);
  useEffect(() => {
    setImgError(false);
  }, [productImage]);

  const [showAllEntries, setShowAllEntries] = useState(false);
  const [showGraphs, setShowGraphs] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (!data || insufficientData) return; // safety guard — button is also disabled in JSX
    setExporting(true);
    try {
      await exportSpcToExcel(data);
    } catch (e) {
      alert(e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };
  // const visibleEntries = showAllEntries ? readings : readings.slice(0, 10);
  const tableEntries = [...readings].reverse(); // latest first, for display only — calculation still uses `readings` (chronological) untouched
  const visibleEntries = showAllEntries
    ? tableEntries
    : tableEntries.slice(0, 10);

  // ── Weight Trend (company-wide, NOT product-specific) — separate API,
  // per earlier decision: /weight-entries/dashboard covers overview widgets,
  // spc-data covers product-specific widgets. Two independent data sources. ──
  const [trendRange, setTrendRange] = useState("today");
  const [trendData, setTrendData] = useState([]);
  useEffect(() => {
    weightAPI
      .dashboard(trendRange)
      .then((res) => setTrendData(res.data?.trend || []))
      .catch(() => {});
  }, [trendRange]);

  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const isMobile = width < 768;

  if (loading && !data) {
    return (
      <div style={{ padding: 28, color: "var(--text2)", textAlign: "center" }}>
        Loading dashboard data...
      </div>
    );
  }
  if (products.length === 0) {
    return (
      <div style={{ padding: 28, color: "var(--text2)", textAlign: "center" }}>
        No part found. Please configure part in the Masters page.
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? 14 : 28, position: "relative" }}>
      {/* Theme toggle — fixed top-right corner of Dashboard */}
      {/* <div
        onClick={toggleTheme}
        role="switch"
        aria-checked={theme === "light"}
        title={
          theme === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"
        }
        style={{
          position: "absolute",
          top: isMobile ? 14 : 28,
          right: isMobile ? 14 : 28,
          zIndex: 5,
          width: 52,
          height: 26,
          borderRadius: 999,
          background: "var(--surface2)",
          border: "1px solid var(--border2)",
          cursor: "pointer",
        }}
      >
        <Sun
          size={12}
          style={{
            position: "absolute",
            left: 6,
            top: "50%",
            transform: "translateY(-50%)",
            color: theme === "light" ? "var(--warn)" : "var(--text3)",
          }}
        />
        <Moon
          size={12}
          style={{
            position: "absolute",
            right: 6,
            top: "50%",
            transform: "translateY(-50%)",
            color: theme === "dark" ? "var(--accent)" : "var(--text3)",
          }}
        />
        <div
          style={{
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: theme === "dark" ? "var(--accent)" : "var(--warn)",
            position: "absolute",
            top: 2,
            left: theme === "dark" ? 28 : 2,
            transition: "left 0.2s ease, background 0.2s ease",
            boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
          }}
        />
      </div> */}

      {/* ── Header ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 14,
        }}
      >
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>
            Real Time Monotoring CP/CPK
          </h1>
          {/* <p style={{ color: "var(--text2)", fontSize: 13, marginTop: 2 }}>
            Real-time SPC & Production Analytics
          </p> */}
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 10,
          }}
        >
          {/* Product Dropdown */}
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{ fontSize: 12, color: "var(--text2)", fontWeight: 600 }}
            >
              Part:
            </span>
            <select
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
              style={{
                padding: "6px 14px",
                borderRadius: 8,
                background: "var(--surface2, #1C2030)",
                border: "1px solid var(--border2, #2E3448)",
                color: "var(--text)",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                outline: "none",
              }}
            >
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.code})
                </option>
              ))}
            </select>
          </div>

          {/* Date + Time Filter — same rules as the Template page */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{ fontSize: 12, color: "var(--text2)", fontWeight: 600 }}
              >
                Date:
              </span>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                max={todayStr()}
                style={{
                  padding: "6px 10px",
                  borderRadius: 8,
                  background: "var(--surface2, #1C2030)",
                  border: "1px solid var(--border2, #2E3448)",
                  color: "var(--text)",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  outline: "none",
                  colorScheme: theme === "dark" ? "dark" : "light",
                }}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{ fontSize: 12, color: "var(--text2)", fontWeight: 600 }}
              >
                From:
              </span>
              <input
                type="time"
                value={fromTime}
                onChange={(e) => setFromTime(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: 8,
                  background: "var(--surface2, #1C2030)",
                  border: "1px solid var(--border2, #2E3448)",
                  color: "var(--text)",
                  fontSize: 12,
                  fontWeight: 600,
                  colorScheme: theme === "dark" ? "dark" : "light",
                }}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{ fontSize: 12, color: "var(--text2)", fontWeight: 600 }}
              >
                To:
              </span>
              <input
                type="time"
                value={toTime}
                onChange={(e) => setToTime(e.target.value)}
                style={{
                  padding: "6px 10px",
                  borderRadius: 8,
                  background: "var(--surface2, #1C2030)",
                  border: "1px solid var(--border2, #2E3448)",
                  color: "var(--text)",
                  fontSize: 12,
                  fontWeight: 600,
                  colorScheme: theme === "dark" ? "dark" : "light",
                }}
              />
            </div>
            <button
              onClick={() => {
                setDate(todayStr());
                setFromTime("");
                setToTime("");
              }}
              style={{
                padding: "6px 12px",
                borderRadius: 8,
                background: "var(--surface2)",
                border: "1px solid var(--border2)",
                color: "var(--text2)",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Reset to Today
            </button>
          </div>

          {lastUpdated && (
            <span style={{ fontSize: 11, color: "var(--text3)" }}>
              Updated: {lastUpdated.toLocaleTimeString()}
              {live && date === todayStr() && !fromTime && !toTime && (
                <span style={{ color: "var(--success)", marginLeft: 6 }}>
                  ● Live
                </span>
              )}
            </span>
          )}

          <button
            onClick={() => fetchDashboardData(false)}
            title="Refresh"
            style={{
              background: "none",
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: "8px",
              cursor: "pointer",
              color: "var(--text2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <RefreshCw size={14} />
          </button>

          <button
            onClick={handleExport}
            disabled={insufficientData || exporting}
            title={
              insufficientData
                ? "Need 50+ readings before Cp/Cpk is calculated"
                : "Export to Excel"
            }
            style={{
              background: insufficientData
                ? "var(--surface2)"
                : "var(--accent)",
              border:
                "1px solid " +
                (insufficientData ? "var(--border2)" : "var(--accent)"),
              borderRadius: 8,
              padding: "8px 16px",
              cursor: insufficientData || exporting ? "not-allowed" : "pointer",
              color: insufficientData ? "var(--text3)" : "#0D0F14",
              opacity: insufficientData ? 0.5 : 1,
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            <FileSpreadsheet size={14} />
            {exporting ? "Exporting..." : "Export Excel"}
          </button>
        </div>
      </div>

      {err && (
        <div
          style={{
            padding: "12px 18px",
            background: "rgba(255,77,106,0.1)",
            border: "1px solid var(--danger)",
            borderRadius: 10,
            color: "var(--danger)",
            marginBottom: 20,
          }}
        >
          {err}
        </div>
      )}

      {/* ── 1. KPI Cards ── */}
      {/* <div
        className="dashboard-cards-grid"
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4,1fr)",
          gap: 14,
          marginBottom: 20,
        }}
      > */}
      <div
        className="dashboard-cards-grid"
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
          gap: 14,
          marginBottom: 20,
        }}
      >
        <KPICard
          label={`Total (${date})`}
          value={totalCount}
          icon={Activity}
          color="var(--accent)"
        />

        {/* <KPICard
          label="Pass"
          value={totalPassCount}
          icon={CheckCircle}
          color="var(--success)"
        /> */}
        {/* <KPICard
          label="Failed"
          value={totalFailCount}
          icon={AlertTriangle}
          color="var(--danger)"
        /> */}
        <KPICard
          label="Avg Weight"
          value={avgWeight}
          unit={avgWeight !== "—" ? "g" : ""}
          icon={TrendingUp}
          color="var(--accent2)"
        />
      </div>

      {/* ── 2. Cp/Cpk, Mean, Sigma Row ── */}
      <div
        style={{
          background: "var(--surface, #151820)",
          padding: "16px 20px",
          borderRadius: 12,
          border: "1px solid var(--border, #252A38)",
          marginBottom: 20,
        }}
      >
        <div
          // style={{
          //   display: "grid",
          //   gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1.2fr",
          //   gap: 16,
          //   alignItems: "center",
          // }}
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: 16,
            alignItems: "center",
          }}
        >
          {/* Cp Column */}
          <div
            style={{
              textAlign: "center",
              borderRight: "1px solid var(--border, #252A38)",
              paddingRight: 12,
            }}
          >
            <div
              style={{
                fontSize: 13,
                color: "var(--text2, #8892AA)",
                fontWeight: 600,
              }}
            >
              Cp
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: 800,
                color: verdictColor(cpVerdict),
                margin: "2px 0",
              }}
            >
              {cp !== null ? cp : "—"}
            </div>
            <span
              style={{
                fontSize: 10,
                padding: "2px 10px",
                borderRadius: 10,
                background: `${verdictColor(cpVerdict)}26`,
                color: verdictColor(cpVerdict),
                border: `1px solid ${verdictColor(cpVerdict)}`,
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              {cpVerdict || "NEED 50 ENTR."}
            </span>
          </div>

          {/* Cpk Column */}
          <div
            style={{
              textAlign: "center",
              borderRight: isMobile
                ? "none"
                : "1px solid var(--border, #252A38)",
              paddingRight: 12,
            }}
          >
            <div
              style={{
                fontSize: 13,
                color: "var(--text2, #8892AA)",
                fontWeight: 600,
              }}
            >
              Cpk
            </div>
            <div
              style={{
                fontSize: 28,
                fontWeight: 800,
                color: verdictColor(cpkVerdict),
                margin: "2px 0",
              }}
            >
              {cpk !== null ? cpk : "—"}
            </div>
            <span
              style={{
                fontSize: 10,
                padding: "2px 10px",
                borderRadius: 10,
                background: `${verdictColor(cpkVerdict)}26`,
                color: verdictColor(cpkVerdict),
                border: `1px solid ${verdictColor(cpkVerdict)}`,
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            >
              {cpkVerdict || "NEED 50 ENTR."}
            </span>
          </div>

          {/* Sigma & Mean */}
          {/* <div
            style={{
              fontSize: 13,
              color: "var(--text2, #8892AA)",
              paddingLeft: isMobile ? 0 : 8,
              gridColumn: isMobile ? "span 2" : "auto",
              display: "flex",
              flexDirection: "column",
              gap: 6,
              borderTop: isMobile ? "1px solid var(--border, #252A38)" : "none",
              paddingTop: isMobile ? 10 : 0,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Standard Deviation (&sigma;)</span>
              <b style={{ color: "var(--text, #E8ECF4)", fontSize: 14 }}>
                {sigmaDisplay} g
              </b>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>Grand Mean (&mu;)</span>
              <b style={{ color: "var(--text, #E8ECF4)", fontSize: 14 }}>
                {grandMeanDisplay} g
              </b>
            </div>
          </div> */}
        </div>
      </div>

      {/* ── 3. LSL, Target, USL Row ── */}
      <div
        style={{
          background: "var(--surface, #151820)",
          padding: "14px 20px",
          borderRadius: 12,
          border: "1px solid var(--border, #252A38)",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 12,
            textAlign: "center",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                color: "var(--text2, #8892AA)",
                fontWeight: 600,
              }}
            >
              Lower Spec Limit (LSL)
            </div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "var(--text, #E8ECF4)",
                marginTop: 4,
              }}
            >
              {LSL} g
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: 11,
                color: "var(--accent, #00C2FF)",
                fontWeight: 600,
              }}
            >
              Target
            </div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "var(--text, #E8ECF4)",
                marginTop: 4,
              }}
            >
              {target} g
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: 11,
                color: "var(--text2, #8892AA)",
                fontWeight: 600,
              }}
            >
              Upper Spec Limit (USL)
            </div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "var(--text, #E8ECF4)",
                marginTop: 4,
              }}
            >
              {USL} g
            </div>
          </div>
        </div>
      </div>

      {/* ── 4. Product Details, Image, Last Weight Status ── */}
      <div
        style={{
          background: "var(--surface, #151820)",
          padding: "20px",
          borderRadius: 12,
          border: "1px solid var(--border, #252A38)",
          marginBottom: 20,
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1.4fr 1fr 1fr",
          gap: isMobile ? 20 : 16,
          alignItems: "center",
        }}
      >
        <div
          style={{
            fontSize: 12,
            color: "var(--text, #E8ECF4)",
            lineHeight: 1.8,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              borderBottom: "1px solid rgba(255,255,255,0.05)",
              paddingBottom: 4,
            }}
          >
            <span
              style={{
                color: "var(--text2, #8892AA)",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Calendar size={13} color="var(--accent, #00C2FF)" /> Last
              Recorded Date
            </span>
            <b>
              {last ? new Date(last.recorded_at).toLocaleDateString() : "—"}
            </b>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              borderBottom: "1px solid rgba(255,255,255,0.05)",
              paddingBottom: 4,
              marginTop: 4,
            }}
          >
            <span
              style={{
                color: "var(--text2, #8892AA)",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Clock size={13} color="var(--accent, #00C2FF)" /> Last Recorded
              Time
            </span>
            <b>
              {last ? new Date(last.recorded_at).toLocaleTimeString() : "—"}
            </b>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              borderBottom: "1px solid rgba(255,255,255,0.05)",
              paddingBottom: 4,
              marginTop: 4,
            }}
          >
            <span
              style={{
                color: "var(--text2, #8892AA)",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Box size={13} color="var(--accent, #00C2FF)" /> Part Name
            </span>
            <b>{selectedProduct?.name || "—"}</b>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              borderBottom: "1px solid rgba(255,255,255,0.05)",
              paddingBottom: 4,
              marginTop: 4,
            }}
          >
            <span
              style={{
                color: "var(--text2, #8892AA)",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Tag size={13} color="var(--accent, #00C2FF)" /> Part Code
            </span>
            <b>{selectedProduct?.code || "—"}</b>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 4,
            }}
          >
            <span
              style={{
                color: "var(--text2, #8892AA)",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <Barcode size={13} color="var(--accent, #00C2FF)" /> Unique Serial
              No
            </span>
            <b>{last?.serial_number || "—"}</b>
          </div>
        </div>

        {/* Product Image — from spc-data's product.image (full URL, already built by backend) */}
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div
            style={{
              width: 130,
              height: 90,
              borderRadius: 8,
              background: "var(--surface2, #1C2030)",
              overflow: "hidden",
              border: "1px solid var(--border2, #2E3448)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {productImage && !imgError ? (
              <img
                src={productImage}
                alt={selectedProduct?.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={() => setImgError(true)}
              />
            ) : (
              <Box size={36} color="var(--accent, #00C2FF)" />
            )}
          </div>
        </div>

        {/* Weight & Status */}
        <div
          style={{
            textAlign: "center",
            paddingLeft: isMobile ? 0 : 8,
            borderTop: isMobile ? "1px solid var(--border, #252A38)" : "none",
            paddingTop: isMobile ? 12 : 0,
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: "var(--text2, #8892AA)",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
            }}
          >
            <Scale size={14} color="var(--accent, #00C2FF)" /> Last Checked
            Weight
          </div>
          <div
            style={{
              fontSize: 28,
              fontWeight: 800,
              color:
                last?.status === "OK"
                  ? "var(--success, #00E096)"
                  : "var(--danger, #FF4D6A)",
              margin: "4px 0",
            }}
          >
            {last ? `${last.weight} g` : "—"}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--text2, #8892AA)",
              marginBottom: 4,
            }}
          >
            Status
          </div>
          {last ? (
            <span
              style={{
                fontSize: 11,
                padding: "3px 18px",
                borderRadius: 12,
                fontWeight: 700,
                background:
                  last.status === "OK"
                    ? "rgba(0,224,150,0.15)"
                    : "rgba(255,77,106,0.15)",
                color:
                  last.status === "OK"
                    ? "var(--success, #00E096)"
                    : "var(--danger, #FF4D6A)",
                border: `1px solid ${last.status === "OK" ? "var(--success, #00E096)" : "var(--danger, #FF4D6A)"}`,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <CheckCircle2 size={13} />{" "}
              {last.status === "OK" ? "PASS" : "FAIL"}
            </span>
          ) : (
            "—"
          )}
        </div>
      </div>

      {/* ── 5. Real-time Weight Entries Table ── */}
      <div
        className="card"
        style={{ padding: 0, overflow: "hidden", marginBottom: 20 }}
      >
        <div
          style={{
            padding: "14px 20px",
            borderBottom: "1px solid var(--border)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3 style={{ fontSize: 13, fontWeight: 600 }}>
            {`Entries for ${date}`}
            <span
              style={{
                fontSize: 11,
                color: "var(--text3)",
                marginLeft: 8,
                fontWeight: 400,
              }}
            >
              ({checksCount} total entries)
            </span>
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {live && date === todayStr() && !fromTime && !toTime && (
              <span
                style={{
                  fontSize: 11,
                  color: "var(--success)",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "var(--success)",
                    display: "inline-block",
                    animation: "pulse 2s infinite",
                  }}
                />
                Auto-refreshing every 1s
              </span>
            )}
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: "6%", textAlign: "center" }}>#</th>
                <th style={{ width: "18%", textAlign: "center" }}>Date</th>
                <th style={{ width: "18%", textAlign: "center" }}>Time</th>
                <th style={{ width: "26%", textAlign: "center" }}>
                  Serial Number
                </th>
                <th style={{ width: "16%", textAlign: "center" }}>Weight</th>
                <th style={{ width: "16%", textAlign: "center" }}>Result</th>
              </tr>
            </thead>
            <tbody>
              {readings.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      textAlign: "center",
                      padding: 24,
                      color: "var(--text3)",
                    }}
                  >
                    No entries for this period
                  </td>
                </tr>
              ) : (
                visibleEntries.map((e, index) => (
                  <tr key={e.id || index}>
                    <td
                      className="mono text3"
                      style={{ fontSize: 11, textAlign: "center" }}
                    >
                      {index + 1}
                    </td>
                    <td style={{ fontSize: 12, textAlign: "center" }}>
                      {new Date(e.recorded_at).toLocaleDateString()}
                    </td>
                    <td
                      style={{
                        fontSize: 11,
                        color: "var(--text2)",
                        textAlign: "center",
                      }}
                    >
                      {new Date(e.recorded_at).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })}
                    </td>
                    <td
                      className="mono text2"
                      style={{
                        fontWeight: 500,
                        color: "var(--accent)",
                        textAlign: "center",
                      }}
                    >
                      {e.serial_number || "—"}
                    </td>
                    <td
                      className="mono"
                      style={{
                        textAlign: "center",
                        color:
                          e.result === "Pass"
                            ? "var(--success)"
                            : "var(--danger)",
                        fontWeight: 700,
                      }}
                    >
                      {e.weight}g
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <span
                        className={
                          "badge " +
                          (e.result === "Pass"
                            ? "badge-success"
                            : "badge-danger")
                        }
                      >
                        {e.result === "Pass" ? "Pass" : "Fail"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {readings.length > 10 && (
          <div
            style={{
              textAlign: "center",
              padding: "10px 0",
              borderTop: "1px solid var(--border)",
            }}
          >
            <button
              onClick={() => setShowAllEntries((v) => !v)}
              style={{
                background: "var(--surface2)",
                border: "1px solid var(--border2)",
                borderRadius: 8,
                padding: "6px 16px",
                fontSize: 12,
                fontWeight: 600,
                color: "var(--accent)",
                cursor: "pointer",
              }}
            >
              {showAllEntries
                ? "Show Less"
                : `View More (${readings.length - 10} more)`}
            </button>
          </div>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: 20,
          marginBottom: 12,
        }}
      >
        {/* X̄-R Control Chart — raw readings vs Mean/USL/LSL */}
        <div className="card" style={{ padding: 16 }}>
          <h3
            style={{
              fontSize: 13,
              fontWeight: 700,
              marginBottom: 8,
              textAlign: "center",
              color: "var(--text)",
            }}
          >
            X̄ - R CONTROL CHART (WEIGHT)
          </h3>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 16,
              flexWrap: "wrap",
              fontSize: 11,
              color: "var(--text2)",
              marginBottom: 12,
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "var(--accent)",
                  display: "inline-block",
                }}
              />{" "}
              Weight
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span
                style={{
                  width: 14,
                  height: 2,
                  borderTop: "2px dashed var(--success)",
                  display: "inline-block",
                }}
              />{" "}
              Mean
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span
                style={{
                  width: 14,
                  height: 2,
                  borderTop: "2px dashed var(--danger)",
                  display: "inline-block",
                }}
              />{" "}
              USL
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span
                style={{
                  width: 14,
                  height: 2,
                  borderTop: "2px dashed var(--danger)",
                  display: "inline-block",
                }}
              />{" "}
              LSL
            </span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={controlChartData}>
              <XAxis dataKey="time" stroke="var(--text3)" fontSize={9} />
              <YAxis domain={getYDomain()} stroke="var(--text3)" fontSize={9} />
              <Tooltip
                contentStyle={{
                  background: "var(--surface2)",
                  border: "1px solid var(--border2)",
                  color: "var(--text)",
                  borderRadius: 8,
                }}
              />
              <ReferenceLine
                y={USL}
                stroke="var(--danger)"
                strokeDasharray="3 3"
              />
              <ReferenceLine
                y={LSL}
                stroke="var(--danger)"
                strokeDasharray="3 3"
              />
              <ReferenceLine
                y={currentMean}
                stroke="var(--success)"
                strokeDasharray="3 3"
              />
              <Line
                type="monotone"
                dataKey="weight"
                stroke="var(--accent)"
                dot={{ r: 3, fill: "var(--accent)" }}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Histogram — bin counts from spc.histogram (backend) */}
        <div className="card" style={{ padding: 16 }}>
          <h3
            style={{
              fontSize: 13,
              fontWeight: 700,
              marginBottom: 8,
              textAlign: "center",
              color: "var(--text)",
            }}
          >
            WEIGHT DISTRIBUTION (Histogram)
          </h3>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 12,
              flexWrap: "wrap",
              fontSize: 11,
              color: "var(--text2)",
              marginBottom: 12,
            }}
          >
            <span>
              <b style={{ color: "var(--accent)" }}>■</b> Bins Count
            </span>
            <span>
              <b style={{ color: "var(--success)" }}>---</b> Gaussian Bell Curve
            </span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={histogram} barCategoryGap={1}>
              <XAxis dataKey="range" stroke="var(--text3)" fontSize={9} />
              <YAxis stroke="var(--text3)" fontSize={9} />
              <Tooltip
                contentStyle={{
                  background: "var(--surface2)",
                  border: "1px solid var(--border2)",
                  color: "var(--text)",
                  borderRadius: 8,
                }}
              />
              <Bar dataKey="count" fill="var(--accent)" radius={[2, 2, 0, 0]} />
              <Line
                type="monotone"
                dataKey="curve"
                stroke="var(--success)"
                strokeWidth={2.5}
                dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── 6. Graphs (toggle) ── */}
      {/* <div style={{ textAlign: "center", marginBottom: showGraphs ? 20 : 0 }}>
        <button
          onClick={() => setShowGraphs((v) => !v)}
          style={{
            background: "var(--surface2)",
            border: "1px solid var(--border2)",
            borderRadius: 8,
            padding: "10px 24px",
            fontSize: 13,
            fontWeight: 600,
            color: "var(--accent)",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          {showGraphs ? "Hide More Graphs ▲" : "Show More Graphs ▼"}
        </button>
      </div> */}
      {/* {showGraphs && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: 20,
          }}
        >
          <div className="card" style={{ padding: 16 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 10,
                marginBottom: 8,
              }}
            >
              <h3
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  textAlign: "center",
                  color: "var(--text)",
                  margin: 0,
                }}
              >
                WEIGHT TREND
              </h3>
              <div
                style={{
                  display: "flex",
                  gap: 4,
                  background: "var(--surface2)",
                  padding: 3,
                  borderRadius: 6,
                }}
              >
                {["today", "week", "month"].map((r) => (
                  <button
                    key={r}
                    onClick={() => setTrendRange(r)}
                    style={{
                      padding: "3px 10px",
                      borderRadius: 5,
                      fontSize: 10,
                      fontWeight: 600,
                      cursor: "pointer",
                      border: "none",
                      textTransform: "capitalize",
                      background:
                        trendRange === r ? "var(--accent)" : "transparent",
                      color: trendRange === r ? "#0D0F14" : "var(--text3)",
                    }}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 12,
                flexWrap: "wrap",
                fontSize: 11,
                color: "var(--text2)",
                marginBottom: 12,
              }}
            >
              <span>
                <b style={{ color: "var(--accent2)" }}>●</b> Avg Weight
              </span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={trendData}>
                <XAxis dataKey="label" stroke="var(--text3)" fontSize={9} />
                <YAxis
                  domain={["auto", "auto"]}
                  stroke="var(--text3)"
                  fontSize={9}
                />
                <Tooltip
                  contentStyle={{
                    background: "var(--surface2)",
                    border: "1px solid var(--border2)",
                    color: "var(--text)",
                    borderRadius: 8,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="weight"
                  stroke="var(--accent2)"
                  dot={{ r: 3, fill: "var(--accent2)" }}
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="card" style={{ padding: 16 }}>
            <h3
              style={{
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 16,
                color: "var(--text)",
                textAlign: "center",
              }}
            >
              RESULT DISTRIBUTION
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {pieData.map((e, i) => (
                    <Cell key={i} fill={e.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: "var(--surface)",
                    border: "1px solid var(--border2)",
                    borderRadius: 8,
                  }}
                  itemStyle={{ color: "var(--text)" }}
                />
                <Legend
                  formatter={(v) => (
                    <span style={{ fontSize: 12, color: "var(--text2)" }}>
                      {v}
                    </span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )} */}
    </div>
  );
}
